import { useRef, useState } from 'react'
import Modal from 'react-modal'
import { IconButton, TextField } from '@mui/material'
import { Close } from '@mui/icons-material'
import LoadingIndicator from '../../common/LoadingIndicator'
import { describeRefundReason } from '../../common/RefundPill'
import formatMoney, { formatInstant, toFiniteAmount } from '../../utils/formatMoney'
import WalletBalanceCard from './WalletBalanceCard'
import { PreLobbyInfo } from './UserLobbies'
import usePermissions from '../../hooks/usePermissions'
import {
  UNFREEZE_NOTE_MAX_LENGTH,
  unfreezeWallet,
  walletFreezes,
  walletHolds,
  walletUnfreezes
} from './wallet'

const INITIATOR_LABELS = {
  passenger: 'pasajero',
  admin: 'admin',
  system: 'sistema'
}

const FREEZE_STATUS_LABELS = {
  charged_back: 'Contracargo',
  in_mediation: 'Disputa en Mercado Pago'
}

const FREEZE_REASON_LABELS = {
  chargeback: 'Contracargo'
}

const UNFREEZE_NOTICES = {
  unfrozen: { tone: 'emerald', text: 'Saldo descongelado.' },
  unfrozenStillFrozen: {
    tone: 'amber',
    text: 'Se descongeló, pero queda otro congelamiento: revisalo antes de descongelarlo.'
  },
  stillFrozen: { tone: 'amber', text: 'El saldo sigue congelado. Revisalo de nuevo.' },
  notFrozen: { tone: 'neutral', text: 'El saldo ya no estaba congelado.' }
}

const unfreezeNoticeOf = result => {
  if (result.stillFrozen) {
    return result.unfrozen ? UNFREEZE_NOTICES.unfrozenStillFrozen : UNFREEZE_NOTICES.stillFrozen
  }
  return result.unfrozen ? UNFREEZE_NOTICES.unfrozen : UNFREEZE_NOTICES.notFrozen
}

const NOTICE_TONES = {
  emerald: 'bg-emerald/10 text-emerald_deep ring-emerald/30 dark:bg-emerald-500/10 dark:text-emerald-400',
  amber: 'bg-amber/[0.18] text-amber-700 ring-amber/40 dark:bg-amber-500/10 dark:text-amber-400',
  neutral: 'bg-ink/[0.05] text-espresso ring-ink/10 dark:bg-dk-raised dark:text-gray-200 dark:ring-dk-border'
}

export const freezeReasonLabel = frozen =>
  FREEZE_STATUS_LABELS[frozen?.status] || FREEZE_REASON_LABELS[frozen?.reason] || frozen?.reason || 'Sin motivo'

const modalStyles = {
  content: {
    top: 40,
    bottom: 40,
    left: 16,
    right: 16,
    maxWidth: 760,
    marginLeft: 'auto',
    marginRight: 'auto',
    borderRadius: '1.5rem',
    padding: '1.5rem 1.75rem',
    display: 'flex',
    flexDirection: 'column'
  },
  overlay: {
    zIndex: 90,
    backgroundColor: 'rgba(0,0,0,0.5)'
  }
}

export const initiatorLabel = (initiatedBy, initiatedById) => {
  const type = typeof initiatedBy === 'string' ? initiatedBy : initiatedBy?.type
  if (!type) return null
  const label = INITIATOR_LABELS[type] || type
  const id = initiatedBy && typeof initiatedBy === 'object' ? initiatedBy.id : initiatedById
  return type === 'admin' && typeof id === 'string' && id ? `${label} · ${id}` : label
}

const signedMoney = (amount, currency) => {
  const value = toFiniteAmount(amount)
  if (value === null) return null
  return `${value < 0 ? '−' : '+'}${formatMoney(Math.abs(value), currency)}`
}

const movementTitle = movement => {
  if (movement.type === 'payment') return 'Pago de viaje'
  if (movement.type && movement.type !== 'refund') return 'Movimiento'
  return movement.partial ? 'Reembolso parcial' : 'Reembolso'
}

const movementAmountTone = movement =>
  toFiniteAmount(movement.amount) < 0
    ? 'text-espresso dark:text-gray-100'
    : 'text-emerald_deep dark:text-emerald-400'

const copyToClipboard = value => {
  if (navigator.clipboard) navigator.clipboard.writeText(String(value))
}

function TripLinks({ transactionId, lobbyId, onOpenLobby }) {
  if (!transactionId && !(lobbyId && onOpenLobby)) return null
  return (
    <div className='flex flex-wrap items-center gap-2 mt-1'>
      {transactionId && (
        <button
          type='button'
          onClick={() => copyToClipboard(transactionId)}
          title='Copiar ID de la transacción'
          className='font-mono text-[11px] text-warm_gray dark:text-gray-400 bg-ink/[0.04] dark:bg-dk-raised hover:bg-ink/[0.08] dark:hover:bg-dk-hover rounded-md px-2 py-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/30'
        >
          {transactionId}
        </button>
      )}
      {lobbyId && onOpenLobby && (
        <button
          type='button'
          onClick={() => onOpenLobby(lobbyId)}
          className='rounded-full px-2.5 py-0.5 text-xs font-bold text-coral_deep dark:text-coral hover:bg-coral/[0.1] dark:hover:bg-red-500/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/30'
        >
          Ver viaje →
        </button>
      )}
    </div>
  )
}

function Route({ origin, destination }) {
  if (!origin && !destination) return null
  return (
    <span className='text-sm text-espresso/80 dark:text-gray-300 truncate'>
      {origin || '—'} → {destination || '—'}
    </span>
  )
}

function MovementRow({ movement, onOpenLobby }) {
  const date = formatInstant(movement.created, movement.currency)
  const initiator = initiatorLabel(movement.initiated_by, movement.initiated_by_id)
  const reason = describeRefundReason({
    reason: movement.reason,
    partial: movement.partial,
    initiator: movement.initiated_by
  })
  const meta = [date, initiator && `Por: ${initiator}`].filter(Boolean).join(' · ')

  return (
    <li className='flex items-start justify-between gap-4 py-3.5 border-b border-warm_border/70 dark:border-dk-border last:border-b-0'>
      <div className='min-w-0 flex flex-col gap-0.5'>
        <div className='flex flex-wrap items-baseline gap-x-2'>
          <span className='text-sm font-semibold text-espresso dark:text-gray-100'>{movementTitle(movement)}</span>
          {reason && <span className='text-xs text-warm_gray dark:text-gray-400'>{reason}</span>}
        </div>
        <Route origin={movement.origin} destination={movement.destination} />
        {meta && <span className='text-xs tabular-nums text-warm_gray dark:text-gray-400'>{meta}</span>}
        {movement.note && (
          <p className='mt-0.5 text-xs text-espresso dark:text-gray-300 break-words'>
            <span className='font-semibold'>Nota: </span>
            {movement.note}
          </p>
        )}
        <TripLinks
          transactionId={movement.transaction_id}
          lobbyId={movement.lobby_id}
          onOpenLobby={onOpenLobby}
        />
      </div>
      <span className={`shrink-0 text-base font-bold tabular-nums ${movementAmountTone(movement)}`}>
        {signedMoney(movement.amount, movement.currency)}
      </span>
    </li>
  )
}

function HoldRow({ hold }) {
  const created = formatInstant(hold.created, hold.currency)
  const expires = formatInstant(hold.expires_at, hold.currency)
  const meta = [created && `Desde ${created}`, expires && `Vence ${expires}`].filter(Boolean).join(' · ')
  return (
    <li className='flex items-start justify-between gap-4 py-3 border-b border-warm_border/70 dark:border-dk-border last:border-b-0'>
      <div className='min-w-0 flex flex-col gap-0.5'>
        <span className='text-sm font-semibold text-espresso dark:text-gray-100'>Reserva para un pago en curso</span>
        {meta && <span className='text-xs tabular-nums text-warm_gray dark:text-gray-400'>{meta}</span>}
        <TripLinks transactionId={hold.transaction_id} />
      </div>
      <span className='shrink-0 text-base font-bold tabular-nums text-espresso dark:text-gray-100'>
        {formatMoney(hold.amount, hold.currency)}
      </span>
    </li>
  )
}

function UnfreezeRow({ unfreeze }) {
  const currency = unfreeze.currencies[0]
  const date = formatInstant(unfreeze.at, currency)
  const frozenSince = formatInstant(unfreeze.frozen?.at, currency)
  const meta = [date, `Por: ${initiatorLabel('admin', unfreeze.by)}`].filter(Boolean).join(' · ')
  return (
    <li className='flex flex-col gap-0.5 py-3 border-b border-warm_border/70 dark:border-dk-border last:border-b-0'>
      <div className='flex flex-wrap items-baseline gap-x-2'>
        <span className='text-sm font-semibold text-espresso dark:text-gray-100'>Descongelado</span>
        {unfreeze.frozen && (
          <span className='text-xs text-warm_gray dark:text-gray-400'>
            {freezeReasonLabel(unfreeze.frozen)}
            {frozenSince ? ` · congelado el ${frozenSince}` : ''}
          </span>
        )}
      </div>
      <span className='text-xs tabular-nums text-warm_gray dark:text-gray-400'>{meta}</span>
      {unfreeze.note && (
        <p className='mt-0.5 text-xs text-espresso dark:text-gray-300 break-words'>
          <span className='font-semibold'>Nota: </span>
          {unfreeze.note}
        </p>
      )}
      <TripLinks transactionId={unfreeze.frozen?.transaction_id} />
    </li>
  )
}

function FreezeDetail({ frozen }) {
  const currency = frozen.currencies?.[0]
  const date = formatInstant(frozen.at, currency)
  return (
    <div className='flex flex-col gap-0.5 text-xs text-espresso dark:text-gray-200'>
      <div>
        <span className='font-semibold'>Motivo: </span>
        {freezeReasonLabel(frozen)}
      </div>
      {date && (
        <div className='tabular-nums'>
          <span className='font-semibold'>Desde: </span>
          {date}
        </div>
      )}
      {frozen.payment_id && (
        <div className='tabular-nums'>
          <span className='font-semibold'>Pago de Mercado Pago: </span>
          {frozen.payment_id}
        </div>
      )}
      {frozen.transaction_id && (
        <div>
          <span className='font-semibold'>Transacción:</span>
          <TripLinks transactionId={frozen.transaction_id} />
        </div>
      )}
    </div>
  )
}

function UnfreezeForm({ userId, frozenAt, onCancel, onFinished }) {
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const submittingRef = useRef(false)
  const trimmedNote = note.trim()
  const canSubmit = !submitting && trimmedNote.length > 0

  const submit = async () => {
    if (submittingRef.current || !canSubmit) return
    submittingRef.current = true
    setSubmitting(true)
    setError(null)
    const result = await unfreezeWallet(userId, trimmedNote, frozenAt)
    setSubmitting(false)
    submittingRef.current = false
    if (result.ok) {
      onFinished(unfreezeNoticeOf(result))
      return
    }
    if (result.uncertain || result.changed) {
      onFinished({ tone: 'amber', text: result.error })
      return
    }
    setError(result.error)
  }

  return (
    <div className='flex flex-col gap-2 pt-1'>
      <TextField
        label='Nota para descongelar (caso, ticket o motivo)'
        value={note}
        onChange={event => setNote(event.target.value)}
        required
        multiline
        minRows={2}
        size='small'
        inputProps={{ maxLength: UNFREEZE_NOTE_MAX_LENGTH }}
        helperText={`Obligatoria. Queda registrada. ${note.length}/${UNFREEZE_NOTE_MAX_LENGTH}`}
      />
      {error && (
        <p role='alert' className='text-xs font-medium text-coral_deep dark:text-red-400'>
          {error}
        </p>
      )}
      <div className='flex flex-wrap items-center justify-end gap-2'>
        <button
          type='button'
          onClick={onCancel}
          disabled={submitting}
          className='rounded-full px-4 py-2 text-sm font-bold text-espresso dark:text-gray-200 hover:bg-ink/[0.05] dark:hover:bg-dk-hover disabled:opacity-50 transition-colors'
        >
          Cancelar
        </button>
        <button
          type='button'
          onClick={submit}
          disabled={!canSubmit}
          aria-busy={submitting}
          className='rounded-full px-4 py-2 text-sm font-bold bg-coral text-white hover:bg-coral_deep disabled:bg-gray-300 dark:disabled:bg-dk-hover dark:disabled:text-gray-500 disabled:cursor-not-allowed transition-colors'
        >
          {submitting ? 'Descongelando…' : 'Confirmar'}
        </button>
      </div>
    </div>
  )
}

function UnfreezeButton({ onClick }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className='rounded-full px-4 py-1.5 text-sm font-bold text-coral_deep dark:text-coral ring-1 ring-coral/40 hover:bg-coral/[0.08] transition-colors'
    >
      Descongelar
    </button>
  )
}

const UNLISTED_FREEZE = 'unlisted'

function FrozenNotice({ freezes, userId, canUnfreeze, onUnfrozen }) {
  const [unfreezing, setUnfreezing] = useState(null)
  const canStart = canUnfreeze && Boolean(userId) && unfreezing === null

  const renderForm = frozenAt => (
    <UnfreezeForm
      userId={userId}
      frozenAt={frozenAt}
      onCancel={() => setUnfreezing(null)}
      onFinished={notice => {
        setUnfreezing(null)
        onUnfrozen(notice)
      }}
    />
  )

  return (
    <section
      aria-label='Saldo congelado'
      className='rounded-2xl bg-coral/[0.08] dark:bg-red-500/10 ring-1 ring-coral/30 dark:ring-red-400/30 px-5 py-4 text-left flex flex-col gap-2'
    >
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <span className='text-sm font-bold text-coral_deep dark:text-red-400'>Saldo congelado</span>
        {canStart && freezes.length === 0 && <UnfreezeButton onClick={() => setUnfreezing(UNLISTED_FREEZE)} />}
      </div>
      <p className='text-xs text-espresso dark:text-gray-300'>
        No puede pagar con saldo. Los reembolsos le siguen llegando.
      </p>
      {freezes.map((frozen, index) => {
        const key = `${frozen.at}-${index}`
        return (
          <div key={key} className='flex flex-col gap-2'>
            <div className='flex flex-wrap items-start justify-between gap-2'>
              <FreezeDetail frozen={frozen} />
              {canStart && <UnfreezeButton onClick={() => setUnfreezing(key)} />}
            </div>
            {unfreezing === key && renderForm(frozen.at)}
          </div>
        )
      })}
      {unfreezing === UNLISTED_FREEZE && renderForm(undefined)}
    </section>
  )
}

function PendingRow({ item, onOpenLobby }) {
  const date = formatInstant(item.created, item.currency)
  return (
    <li className='flex items-start justify-between gap-4 py-3 border-b border-amber/30 dark:border-amber-400/20 last:border-b-0'>
      <div className='min-w-0 flex flex-col gap-0.5'>
        <span className='text-sm font-semibold text-espresso dark:text-gray-100'>Acreditación en proceso</span>
        <Route origin={item.origin} destination={item.destination} />
        {date && <span className='text-xs tabular-nums text-warm_gray dark:text-gray-400'>{date}</span>}
        <TripLinks transactionId={item.transaction_id} lobbyId={item.lobby_id} onOpenLobby={onOpenLobby} />
      </div>
      <span className='shrink-0 text-base font-bold tabular-nums text-amber-700 dark:text-amber-400'>
        {signedMoney(item.amount, item.currency)}
      </span>
    </li>
  )
}

function SectionTitle({ children }) {
  return (
    <h3 className='text-[11px] uppercase tracking-wider font-bold text-warm_gray dark:text-gray-400'>{children}</h3>
  )
}

function WalletBody({ wallet, userId, canUnfreeze, onReload, onOpenLobby }) {
  const [notice, setNotice] = useState(null)
  const movements = wallet.movements || []
  const pending = wallet.pending || []
  const holds = walletHolds(wallet)
  const unfreezes = walletUnfreezes(wallet)

  const handleUnfrozen = result => {
    setNotice(result)
    if (onReload) onReload()
  }

  return (
    <div className='flex flex-col gap-6 min-h-0 overflow-auto pr-1'>
      <WalletBalanceCard wallet={wallet} />
      {notice && (
        <p role='status' className={`rounded-2xl px-4 py-3 text-sm font-medium ring-1 ${NOTICE_TONES[notice.tone]}`}>
          {notice.text}
        </p>
      )}
      {wallet.frozen && (
        <FrozenNotice
          freezes={walletFreezes(wallet)}
          userId={userId}
          canUnfreeze={canUnfreeze}
          onUnfrozen={handleUnfrozen}
        />
      )}
      {holds.length > 0 && (
        <section className='flex flex-col gap-1'>
          <SectionTitle>Reservado para pagos en curso ({holds.length})</SectionTitle>
          <ul>
            {holds.map((hold, index) => (
              <HoldRow key={hold.id || index} hold={hold} />
            ))}
          </ul>
        </section>
      )}
      {pending.length > 0 && (
        <section className='flex flex-col gap-1'>
          <SectionTitle>En proceso ({pending.length})</SectionTitle>
          <ul>
            {pending.map((item, index) => (
              <PendingRow key={item.transaction_id || index} item={item} onOpenLobby={onOpenLobby} />
            ))}
          </ul>
        </section>
      )}
      <section className='flex flex-col gap-1'>
        <SectionTitle>Movimientos{movements.length > 0 ? ` (${movements.length})` : ''}</SectionTitle>
        {movements.length > 0 ? (
          <ul>
            {movements.map((movement, index) => (
              <MovementRow key={movement.id || index} movement={movement} onOpenLobby={onOpenLobby} />
            ))}
          </ul>
        ) : (
          <p className='py-3 text-sm text-warm_gray dark:text-gray-400 max-w-md'>
            Todavía no tiene movimientos. Cuando reciba un reembolso a su saldo Viatik, lo vas a ver acá con el viaje y
            quién lo pidió.
          </p>
        )}
      </section>
      {unfreezes.length > 0 && (
        <section className='flex flex-col gap-1'>
          <SectionTitle>Descongelamientos ({unfreezes.length})</SectionTitle>
          <ul>
            {unfreezes.map((unfreeze, index) => (
              <UnfreezeRow key={`${unfreeze.at}-${index}`} unfreeze={unfreeze} />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

export function WalletContent({ wallet, loading, error, userId, canUnfreeze, onReload, onOpenLobby }) {
  if (!wallet && loading) {
    return (
      <div className='flex flex-1 items-center justify-center py-16'>
        <LoadingIndicator />
      </div>
    )
  }
  if (!wallet) {
    return (
      <div className='flex flex-col items-start gap-3 py-6' role='alert'>
        <p className='text-sm font-medium text-coral_deep dark:text-red-400'>
          {error === 'unavailable'
            ? 'El saldo todavía no está disponible en este entorno.'
            : 'No pudimos cargar el saldo.'}
        </p>
        {onReload && (
          <button
            type='button'
            onClick={onReload}
            className='rounded-full px-4 py-2 text-sm font-bold text-coral_deep dark:text-coral ring-1 ring-coral/30 hover:bg-coral/[0.08] transition-colors'
          >
            Reintentar
          </button>
        )}
      </div>
    )
  }
  return (
    <WalletBody
      wallet={wallet}
      userId={userId}
      canUnfreeze={canUnfreeze}
      onReload={onReload}
      onOpenLobby={onOpenLobby}
    />
  )
}

export default function UserWallet({ visible, onClose, userId, wallet, loading, error, reload }) {
  const [selectedLobby, setSelectedLobby] = useState({})
  const { hasPermission } = usePermissions()
  const canReadLobby = hasPermission('read-lobby')
  const canUnfreeze = hasPermission('edit-user')

  return (
    <>
      <Modal isOpen={visible} onRequestClose={onClose} style={modalStyles} contentLabel='Saldo Viatik'>
        <div className='flex flex-col h-full min-h-0 text-espresso dark:text-gray-100'>
          <div className='flex items-center justify-between gap-4 mb-4'>
            <div className='flex items-center gap-3'>
              <h2 className='text-xl font-bold tracking-tight'>Saldo Viatik</h2>
              {loading && wallet && <LoadingIndicator size={14} />}
            </div>
            <IconButton aria-label='Cerrar' onClick={onClose}>
              <Close />
            </IconButton>
          </div>
          <WalletContent
            wallet={wallet}
            loading={loading}
            error={error}
            userId={userId}
            canUnfreeze={canUnfreeze}
            onReload={reload}
            onOpenLobby={canReadLobby ? lobbyId => setSelectedLobby({ _id: lobbyId }) : null}
          />
        </div>
      </Modal>
      {Object.keys(selectedLobby).length !== 0 && (
        <PreLobbyInfo lobbyId={selectedLobby._id} setSelectedLobby={setSelectedLobby} />
      )}
    </>
  )
}
